<template>
  <div class="bg-background-light text-slate-800 antialiased h-screen flex flex-col md:flex-row overflow-hidden">
    <ClientSidebar />

    <main class="flex-1 flex flex-col min-h-0 overflow-hidden">

      <!-- Header -->
      <header class="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 flex-shrink-0">
        <div>
          <h2 class="text-lg font-bold text-slate-900">Messages</h2>
          <p class="text-xs text-slate-500">Direct communication with your assigned CPA</p>
        </div>
      </header>

      <!-- Chat Layout -->
      <div class="flex-1 flex overflow-hidden">

        <!-- ── Left: CPA Info Panel ── -->
        <div class="bg-white border-r border-slate-200 flex flex-col
          w-full md:w-72 md:flex-shrink-0
          md:flex"
          :class="showPanel ? 'flex' : 'hidden md:flex'">

          <!-- CPA Card -->
          <div class="p-5 border-b border-slate-100">
            <div class="flex items-center gap-3 mb-3">
              <div class="relative">
                <div class="h-11 w-11 rounded-full bg-[#282F44] flex items-center justify-center flex-shrink-0">
                  <span class="text-sm font-bold text-primary">{{ cpaInitials }}</span>
                </div>
                <span class="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-400 border-2 border-white"></span>
              </div>
              <div>
                <p class="text-sm font-bold text-slate-900">{{ cpaName }}</p>
                <p class="text-xs text-green-500 font-medium">Online · Assigned CPA</p>
              </div>
            </div>
            <p class="text-xs text-slate-400 leading-relaxed">
              Your dedicated tax advisor. Send questions, share documents, or request a call anytime.
            </p>
          </div>

          <!-- Quick Actions -->
          <div class="p-4 border-b border-slate-100">
            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Quick Actions</p>
            <div class="space-y-2">
              <router-link
                to="/client/documents"
                class="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600 text-xs font-semibold transition-colors">
                <span class="material-symbols-outlined !text-[16px] text-primary">upload_file</span>
                Upload Documents
              </router-link>
            </div>
          </div>

          <!-- Conversation Stats -->
          <div class="p-4 flex-1">
            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">This Conversation</p>
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-xs text-slate-500">Total messages</span>
                <span class="text-xs font-bold text-slate-800">{{ messages.length }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-xs text-slate-500">Unread</span>
                <span class="text-xs font-bold" :class="unreadCount > 0 ? 'text-primary' : 'text-slate-800'">
                  {{ unreadCount }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-xs text-slate-500">Attachments</span>
                <span class="text-xs font-bold text-slate-800">{{ attachmentCount }}</span>
              </div>
            </div>
          </div>
          <!-- Mobile: back to messages button -->
          <div class="md:hidden p-4 border-t border-slate-100 mt-auto">
            <button @click="showPanel = false"
              class="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#282F44] text-white text-sm font-semibold rounded-lg hover:bg-[#1a1f2e] transition-colors">
              <span class="material-symbols-outlined !text-[16px]">chat</span>
              Back to Messages
            </button>
          </div>
        </div>

        <!-- ── Right: Chat Window ── -->
        <div class="flex-1 flex flex-col overflow-hidden bg-slate-50" :class="showPanel ? 'hidden md:flex' : 'flex'">
          <!-- Mobile: back to CPA info button -->
          <div class="md:hidden flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-white flex-shrink-0">
            <button @click="showPanel = true"
              class="flex items-center gap-1 text-sm text-slate-500 font-medium">
              <span class="material-symbols-outlined" style="font-size: 18px;">arrow_back</span>
              CPA Info
            </button>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="flex-1 flex flex-col items-center justify-center gap-3">
            <svg class="animate-spin h-8 w-8 text-[#282F44]" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-slate-400 text-sm">Loading messages...</p>
          </div>

          <!-- Empty state -->
          <div v-else-if="messages.length === 0" class="flex-1 flex flex-col items-center justify-center gap-3 text-center px-8">
            <div class="h-16 w-16 rounded-full bg-slate-200 flex items-center justify-center">
              <span class="material-symbols-outlined text-slate-400 !text-[28px]">chat</span>
            </div>
            <p class="text-slate-700 font-semibold">No messages yet</p>
            <p class="text-slate-400 text-sm">Send a message below to start a conversation with your CPA.</p>
          </div>

          <!-- Messages scroll area -->
          <div v-else ref="chatContainer" class="flex-1 overflow-y-auto px-6 py-5 space-y-4">

            <template v-for="(group, date) in groupedMessages" :key="date">

              <!-- Date divider -->
              <div class="flex items-center gap-3 my-4">
                <div class="flex-1 h-px bg-slate-200"></div>
                <span class="text-xs text-slate-400 font-medium px-2">{{ date }}</span>
                <div class="flex-1 h-px bg-slate-200"></div>
              </div>

              <!-- Messages in this group -->
              <div
                v-for="msg in group"
                :key="msg.Id"
                class="flex gap-3"
                :class="msg.SenderType === 'Client' ? 'flex-row-reverse' : 'flex-row'">

                <!-- Avatar -->
                <div
                  class="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                  :class="msg.SenderType === 'Client' ? 'bg-primary' : 'bg-[#282F44]'">
                  <span class="text-xs font-bold" :class="msg.SenderType === 'Client' ? 'text-[#282F44]' : 'text-primary'">
                    {{ msg.SenderType === 'Client' ? clientInitials : cpaInitials }}
                  </span>
                </div>

                <!-- Bubble -->
                <div class="max-w-[65%] flex flex-col gap-1" :class="msg.SenderType === 'Client' ? 'items-end' : 'items-start'">

                  <!-- Sender name + time -->
                  <div class="flex items-center gap-2" :class="msg.SenderType === 'Client' ? 'flex-row-reverse' : 'flex-row'">
                    <span class="text-xs font-semibold text-slate-600">
                      {{ msg.SenderType === 'Client' ? 'You' : msg.Sender || cpaName }}
                    </span>
                    <span class="text-xs text-slate-400">{{ formatTime(msg.Created) }}</span>
                  </div>

                  <!-- Text message bubble -->
                  <div
                    v-if="msg.Message"
                    class="px-4 py-2.5 rounded-2xl text-sm leading-relaxed"
                    :class="msg.SenderType === 'Client'
                      ? 'bg-[#282F44] text-white rounded-tr-sm'
                      : 'bg-white text-slate-800 border border-slate-200 rounded-tl-sm shadow-sm'">
                    {{ msg.Message }}
                  </div>

                  <!-- File attachment bubble -->
                  <a
                    v-if="msg.FileUrl || msg.DriveItemId"
                    :href="buildFileUrl(msg)"
                    target="_blank"
                    class="flex items-center gap-3 px-4 py-3 rounded-2xl border transition-colors"
                    :class="msg.SenderType === 'Client'
                      ? 'bg-[#282F44]/80 border-white/10 hover:bg-[#282F44] rounded-tr-sm'
                      : 'bg-white border-slate-200 hover:bg-slate-50 rounded-tl-sm shadow-sm'">
                    <div
                      class="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      :class="msg.SenderType === 'Client' ? 'bg-white/10' : 'bg-red-50'">
                      <span
                        class="material-symbols-outlined !text-[18px]"
                        :class="msg.SenderType === 'Client' ? 'text-white' : 'text-red-500'">
                        {{ getFileIcon(msg.FileName) }}
                      </span>
                    </div>
                    <div>
                      <p class="text-xs font-semibold truncate max-w-[180px]"
                        :class="msg.SenderType === 'Client' ? 'text-white' : 'text-slate-800'">
                        {{ msg.FileName || 'Attachment' }}
                      </p>
                      <p class="text-xs" :class="msg.SenderType === 'Client' ? 'text-white/50' : 'text-slate-400'">
                        {{ formatFileSize(msg.FileSize) }} · Click to download
                      </p>
                    </div>
                    <span
                      class="material-symbols-outlined !text-[16px] ml-1"
                      :class="msg.SenderType === 'Client' ? 'text-white/50' : 'text-slate-400'">
                      download
                    </span>
                  </a>

                </div>
              </div>
            </template>
          </div>

          <!-- ── Input Area ── -->
          <div class="bg-white border-t border-slate-200 px-5 py-4 flex-shrink-0">

            <!-- File preview (when file selected) -->
            <div v-if="selectedFile" class="flex items-center gap-3 mb-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
              <div class="h-9 w-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                <span class="material-symbols-outlined text-red-500 !text-[18px]">{{ getFileIcon(selectedFile.name) }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold text-slate-800 truncate">{{ selectedFile.name }}</p>
                <p class="text-xs text-slate-400">{{ formatFileSize(selectedFile.size) }}</p>
              </div>
              <button @click="removeFile" class="text-slate-400 hover:text-red-500 transition-colors">
                <span class="material-symbols-outlined !text-[18px]">close</span>
              </button>
            </div>

            <div class="flex items-end gap-3">

              <!-- Attach file button -->
              <label class="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-slate-400 hover:text-primary hover:bg-primary/10 transition-colors cursor-pointer">
                <span class="material-symbols-outlined !text-[20px]">attach_file</span>
                <input ref="fileInput" type="file" class="hidden" @change="onFileSelected" accept=".pdf,.docx,.doc,.xlsx,.xls,.jpg,.jpeg,.png" />
              </label>

              <!-- Message input -->
              <div class="flex-1 relative">
                <textarea
                  v-model="messageText"
                  @keydown.enter.exact.prevent="handleSend"
                  placeholder="Type a message..."
                  rows="1"
                  class="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  style="max-height: 120px; overflow-y: auto;">
                </textarea>
              </div>

              <!-- Send button -->
              <button
                @click="handleSend"
                :disabled="sending || (!messageText.trim() && !selectedFile)"
                class="flex-shrink-0 h-10 w-10 rounded-full bg-[#282F44] hover:bg-[#1a1f2e] flex items-center justify-center transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                <svg v-if="sending" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-else class="material-symbols-outlined text-white !text-[18px]">send</span>
              </button>

            </div>
            <p class="text-xs text-slate-400 mt-2 ml-1">Press Enter to send · Shift+Enter for new line</p>
          </div>

        </div>
      </div>

      <!-- Disclaimer -->
      <p class="px-4 md:px-8 lg:px-12 py-4 text-center leading-relaxed" style="font-size:9px; font-style:italic; color:rgba(0,0,0,0.8);">
        <span class="font-semibold not-italic">Disclaimer:</span> By uploading documents to this portal, you acknowledge that while we employ industry-standard security to protect your data, the transmission of information over the internet is not entirely risk-free. ABC TAX ADVISORY utilizes this third-party platform for your convenience but assumes no responsibility for data security or technical issues arising from its use.
      </p>
    </main>
  </div>
</template>

<script>
import ClientSidebar from '../../components/ClientSidebar.vue';

export default {
  name: 'ClientMessages',
  components: { ClientSidebar },

  data() {
    return {
      showPanel: window.innerWidth >= 768,
      loading: true,
      sending: false,
      messages: [],
      messageText: '',
      selectedFile: null,
      pollInterval: null
    };
  },

  computed: {
    clientRecord() {
      try {
        const token = sessionStorage.getItem('clientSession');
        if (!token) return {};
        const [data] = token.split('.');
        if (!data) return {};
        const payload = JSON.parse(atob(data.replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp || Date.now() > payload.exp) return {};
        return payload;
      } catch { return {}; }
    },

    clientName() {
      return this.clientRecord?.name || 'Client';
    },

    clientInitials() {
      const name = this.clientName;
      const parts = name.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    },

    cpaName() {
      return this.clientRecord?.assignedCPA || 'Mark Tameret';
    },

    cpaInitials() {
      const parts = this.cpaName.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return this.cpaName.substring(0, 2).toUpperCase();
    },

    unreadCount() {
      return this.messages.filter(m => m.SenderType === 'Admin' && !m.IsRead).length;
    },

    attachmentCount() {
      return this.messages.filter(m => m.FileUrl).length;
    },

    // ✅ Group messages by date for dividers
    groupedMessages() {
      const groups = {};
      this.messages.forEach(msg => {
        const date = new Date(msg.Created);
        const now = new Date();
        const diffDays = Math.floor((now - date) / 86400000);
        let label;
        if (diffDays === 0) label = 'Today';
        else if (diffDays === 1) label = 'Yesterday';
        else label = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

        if (!groups[label]) groups[label] = [];
        groups[label].push(msg);
      });
      return groups;
    }
  },

  async mounted() {
    await this.loadMessages();
    // Poll for new messages every 15 seconds
    this.pollInterval = setInterval(() => {
      this.loadMessages(false);
    }, 15000);
  },

  beforeUnmount() {
    if (this.pollInterval) clearInterval(this.pollInterval);
  },

  methods: {
    async loadMessages(showLoader = true) {
      if (showLoader) this.loading = true;
      try {
        const msgs = await this.$sharepoint.getClientMessages(this.clientName);
        // Sort oldest → newest for chat display
        this.messages = msgs.sort((a, b) => new Date(a.Created) - new Date(b.Created));

        // Mark unread admin messages as read
        const unread = this.messages.filter(m => m.SenderType === 'Admin' && !m.IsRead);
        for (const msg of unread) {
          await this.$sharepoint.markMessageRead(msg.Id);
          msg.IsRead = true;
        }

        this.$nextTick(() => this.scrollToBottom());
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        if (showLoader) this.loading = false;
      }
    },

    async handleSend() {
      const text = this.messageText.trim();
      if (!text && !this.selectedFile) return;
      if (this.sending) return;

      this.sending = true;
      try {
        let fileUrl = null;
        let fileName = null;
        let fileSize = null;

        // ✅ Upload attachment first if selected
        let driveItemId = null;
        let driveId = null;
        if (this.selectedFile) {
          const uploaded = await this.$sharepoint.uploadDocument(this.selectedFile, {
            clientName: this.clientName,
            category: 'Uploaded Documents'
          });
          fileUrl = uploaded.FileUrl;
          driveItemId = uploaded.DriveItemId || null;
          driveId = uploaded.DriveId || null;
          fileName = this.selectedFile.name;
          fileSize = this.selectedFile.size;
        }

        // ✅ Send message — store driveItemId|driveId for proxy download (no SP login needed)
        const sent = await this.$sharepoint.sendClientMessage({
          subject: driveItemId ? fileName : `Message from ${this.clientName}`,
          message: text || `Sent a file: ${fileName}`,
          clientName: this.clientName,
          sender: this.clientName,
          senderType: 'Client',
          sendEmail: false,
          driveItemId: driveItemId || null,
          driveId: driveId || null,
          fileSize: fileSize ? Math.round(fileSize) : null
        });

        // Append to local state immediately (DriveItemId/DriveId for proxy URL building)
        this.messages.push({
          ...sent,
          Id: sent.Id || Date.now(),
          Message: text || `Sent a file: ${fileName}`,
          SenderType: 'Client',
          Sender: this.clientName,
          FileUrl: fileUrl,
          DriveItemId: driveItemId,
          DriveId: driveId,
          FileName: fileName,
          FileSize: fileSize,
          IsRead: true,
          Created: new Date().toISOString()
        });

        this.messageText = '';
        this.selectedFile = null;
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';

        this.$nextTick(() => this.scrollToBottom());

      } catch (error) {
        console.error('Error sending message:', error);
        alert('Failed to send message. Please try again.');
      } finally {
        this.sending = false;
      }
    },

    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) this.selectedFile = file;
    },

    removeFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },

    buildFileUrl(msg) {
      const token = sessionStorage.getItem('clientSession') || '';
      if (msg.DriveItemId && msg.DriveId) {
        return `/.netlify/functions/get-file?driveId=${encodeURIComponent(msg.DriveId)}&itemId=${encodeURIComponent(msg.DriveItemId)}&token=${encodeURIComponent(token)}`;
      }
      if (msg.FileUrl && msg.FileUrl.includes('|')) {
        const [itemId, drvId] = msg.FileUrl.split('|');
        return `/.netlify/functions/get-file?driveId=${encodeURIComponent(drvId)}&itemId=${encodeURIComponent(itemId)}&token=${encodeURIComponent(token)}`;
      }
      return msg.FileUrl || '#';
    },

    scrollToBottom() {
      const container = this.$refs.chatContainer;
      if (container) container.scrollTop = container.scrollHeight;
    },

    // ── Helpers ──────────────────────────────────────────────────
    formatTime(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    },

    getFileType(filename) {
      const ext = (filename || '').split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'pdf';
      if (['docx', 'doc'].includes(ext)) return 'word';
      if (['xlsx', 'xls'].includes(ext)) return 'excel';
      if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
      return 'file';
    },

    getFileIcon(filename) {
      const map = {
        pdf: 'picture_as_pdf', word: 'description',
        excel: 'table_view', image: 'image', file: 'insert_drive_file'
      };
      return map[this.getFileType(filename)] || 'insert_drive_file';
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  }
};
</script>